// pipe: 4B.8 (Task 12) — a controlled signature-capture leaf used by the
// pickup-execution view for the three §3.2.43 chain-of-custody signatures
// (releasing/customer + agent/driver + logistics). It is a PURE presentational
// leaf: it imports ONLY @affine/i18n + the css module, so the pickup-exec-view
// stays runtime-service/auth/router-free when it pulls this in.
//
// The pad draws a single freehand stroke set onto a <canvas> and, on pointer-up,
// emits the canvas as a PNG data-URL via `onChange`. "Clear" resets the canvas
// and emits an empty string.
//
// happy-dom caveat: `<canvas>.getContext('2d')` can be null under happy-dom, so
// every context use is guarded (no throw); `toDataURL` is still callable (the
// spec stubs it). The drawing is therefore best-effort visual feedback — the
// onChange contract (a PNG data-URL out) holds even with no real raster context.
import { useI18n } from '@affine/i18n';
import { memo, useCallback, useEffect, useRef } from 'react';

import * as styles from './pickup-exec.css';

export interface SignaturePadProps {
  /** A short human label for this signature role (already-translated). */
  label: string;
  /** The current captured value (a PNG data-URL, or '' when empty). */
  value: string;
  /** Emits the PNG data-URL on pointer-up, or '' on Clear. */
  onChange: (dataUrl: string) => void;
}

// The pad's intrinsic raster size. CSS scales the element down responsively; the
// captured PNG is this fixed size so signatures are legible on the cert artifact.
const PAD_W = 480;
const PAD_H = 160;

// happy-dom returns a partial 2D-context stub whose draw methods may be absent,
// and a real context may be null on an unsupported canvas. Resolve a context only
// when it actually carries the methods we use — so drawing is best-effort visual
// feedback and never throws (the onChange PNG-data-URL contract is what matters).
function drawCtx(
  canvas: HTMLCanvasElement | null
): CanvasRenderingContext2D | null {
  const ctx = canvas?.getContext('2d');
  if (
    ctx &&
    typeof ctx.beginPath === 'function' &&
    typeof ctx.lineTo === 'function' &&
    typeof ctx.stroke === 'function'
  ) {
    return ctx;
  }
  return null;
}

function clearRaster(canvas: HTMLCanvasElement | null): void {
  const ctx = canvas?.getContext('2d');
  if (ctx && typeof ctx.clearRect === 'function') {
    ctx.clearRect(0, 0, PAD_W, PAD_H);
  }
}

// Memoized: the render output depends only on the props (the canvas raster is
// ref-driven, never re-painted by a render), so with stable `onChange` handlers
// a keystroke in the weight/reason/agent-id inputs no longer re-renders the
// three pads. Same markup, same onChange contract.
export const SignaturePad = memo(function SignaturePad({
  label,
  value,
  onChange,
}: SignaturePadProps) {
  const t = useI18n();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  // Whether an ACTUAL mark was made since pointer-down. A bare tap (down→up with
  // no move) leaves this false, so pointer-up emits '' rather than a blank-raster
  // PNG — a tap with no stroke is NOT a signature (it would otherwise pass the
  // server's empty/whitespace check and satisfy the §3.2.43 triple-sig gate with
  // blank evidence).
  const drew = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  // Clear the backing raster when the controlled value is reset to ''. This keeps
  // the visual in sync if the container clears the field (e.g. on a fresh form).
  useEffect(() => {
    if (value === '') {
      clearRaster(canvasRef.current);
    }
  }, [value]);

  const pointAt = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    // Map client coords into the canvas' intrinsic raster space (rect may be
    // CSS-scaled). Guard a zero-size rect (happy-dom) so we never divide by 0.
    const sx = rect.width ? PAD_W / rect.width : 1;
    const sy = rect.height ? PAD_H / rect.height : 1;
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      drawing.current = true;
      drew.current = false;
      last.current = pointAt(e);
    },
    [pointAt]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawing.current) {
        return;
      }
      // A move while drawing is an actual mark — only NOW is this a signature.
      drew.current = true;
      const ctx = drawCtx(canvasRef.current);
      const from = last.current;
      const to = pointAt(e);
      if (ctx && from) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
      last.current = to;
    },
    [pointAt]
  );

  const onPointerUp = useCallback(() => {
    if (!drawing.current) {
      return;
    }
    drawing.current = false;
    last.current = null;
    // Emit a PNG ONLY if an actual stroke was drawn; a bare tap emits '' so a
    // blank-raster PNG can never satisfy the §3.2.43 triple-signature gate.
    const canvas = canvasRef.current;
    if (drew.current && canvas) {
      onChange(canvas.toDataURL('image/png'));
    } else {
      onChange('');
    }
    drew.current = false;
  }, [onChange]);

  const onClear = useCallback(() => {
    clearRaster(canvasRef.current);
    drawing.current = false;
    drew.current = false;
    last.current = null;
    onChange('');
  }, [onChange]);

  return (
    <div className={styles.sigField}>
      <div className={styles.sigHeader}>
        <span className={styles.sigLabel}>{label}</span>
        <button type="button" className={styles.sigClear} onClick={onClear}>
          {t['com.affine.pipe.driver.exec.sig.clear']()}
        </button>
      </div>
      <canvas
        ref={canvasRef}
        className={styles.sigCanvas}
        width={PAD_W}
        height={PAD_H}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        aria-label={label}
      />
    </div>
  );
});
